import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export interface LoadingDocumentsContextValue {
  loadingDocumentIds: Set<string>;
  addLoadingDocuments: (documentIds: string[]) => void;
  removeLoadingDocuments: (documentIds: string[]) => void;
  clearLoadingDocuments: () => void;
  isLoading: (documentId: string) => boolean;
  loadingDocumentsMap: Record<string, boolean>;
}

const LoadingDocumentsContext = createContext<LoadingDocumentsContextValue | null>(null);

export function LoadingDocumentsProvider({ children }: PropsWithChildren) {
  const [loadingDocumentIds, setLoadingDocumentIds] = useState<Set<string>>(new Set());

  const addLoadingDocuments = useCallback((documentIds: string[]) => {
    setLoadingDocumentIds((prev) => {
      const newSet = new Set(prev);
      documentIds.forEach((id) => newSet.add(id));
      return newSet;
    });
  }, []);

  const removeLoadingDocuments = useCallback((documentIds: string[]) => {
    setLoadingDocumentIds((prev) => {
      const newSet = new Set(prev);
      documentIds.forEach((id) => newSet.delete(id));
      return newSet;
    });
  }, []);

  const clearLoadingDocuments = useCallback(() => {
    setLoadingDocumentIds(new Set());
  }, []);

  const isLoading = useCallback(
    (documentId: string) => {
      return loadingDocumentIds.has(documentId);
    },
    [loadingDocumentIds],
  );

  const loadingDocumentsMap = useMemo(() => {
    const map: Record<string, boolean> = {};
    loadingDocumentIds.forEach((id) => {
      map[id] = true;
    });
    return map;
  }, [loadingDocumentIds]);

  const value: LoadingDocumentsContextValue = {
    loadingDocumentIds,
    addLoadingDocuments,
    removeLoadingDocuments,
    clearLoadingDocuments,
    isLoading,
    loadingDocumentsMap,
  };

  // Expose imperative API for sagas
  useEffect(() => {
    externalAddLoadingDocuments = addLoadingDocuments;
    externalRemoveLoadingDocuments = removeLoadingDocuments;
    externalClearLoadingDocuments = clearLoadingDocuments;

    return () => {
      externalAddLoadingDocuments = null;
      externalRemoveLoadingDocuments = null;
      externalClearLoadingDocuments = null;
    };
  }, [addLoadingDocuments, removeLoadingDocuments, clearLoadingDocuments]);

  return (
    <LoadingDocumentsContext.Provider value={value}>{children}</LoadingDocumentsContext.Provider>
  );
}

export function useLoadingDocuments(): LoadingDocumentsContextValue {
  const ctx = useContext(LoadingDocumentsContext);
  if (!ctx) {
    throw new Error('useLoadingDocuments must be used within LoadingDocumentsProvider');
  }
  return ctx;
}

// Imperative API for sagas compatibility
let externalAddLoadingDocuments: ((documentIds: string[]) => void) | null = null;
let externalRemoveLoadingDocuments: ((documentIds: string[]) => void) | null = null;
let externalClearLoadingDocuments: (() => void) | null = null;

export const LoadingDocumentsController = {
  addLoadingDocuments(documentIds: string[]) {
    if (!externalAddLoadingDocuments) {
      console.warn('LoadingDocumentsProvider not mounted yet');
      return;
    }
    externalAddLoadingDocuments(documentIds);
  },

  removeLoadingDocuments(documentIds: string[]) {
    if (!externalRemoveLoadingDocuments) {
      console.warn('LoadingDocumentsProvider not mounted yet');
      return;
    }
    externalRemoveLoadingDocuments(documentIds);
  },

  clearLoadingDocuments() {
    if (!externalClearLoadingDocuments) {
      console.warn('LoadingDocumentsProvider not mounted yet');
      return;
    }
    externalClearLoadingDocuments();
  },
};
